<template>
    <x-container fluid pa-0>
        <x-layout wrap>
            <x-flex v-if="insurerSpecificUpload" xs12 :lg3="!column" class="left">
                <x-card class="panel">
                    <x-card-title>
                        <h3>Upload files</h3>
                    </x-card-title>

                    <x-card-text>
                        <x-form data-testid="specificDocumentForm" ref="specificDocumentForm" v-if="!uploadInProgress">
                            <x-container pa-0>
                                <x-layout wrap>
                                    <x-flex xs4 :lg12="!column" mb-3>
                                        <x-text-field
                                            label="File"
                                            v-model="filename"
                                            placeholder=" "
                                            outline
                                            :rules="[
                                                 v => ruleRequired(v),
                                             ]"
                                            append-icon="cloud_upload"
                                            hide-details
                                            @click="specificFilePicked"
                                        />

                                        <input type="file" ref="specificFile" @change="loadSpecificFile">
                                    </x-flex>

                                    <x-flex xs4 :lg12="!column" mb-3>
                                        <x-select
                                            label="File type"
                                            v-model="uploadDocument.category"
                                            :items="documentCategories"
                                            :rules="[
                                                 v => ruleRequired(v),
                                             ]"
                                            placeholder="File type"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                            hide-details
                                        />
                                    </x-flex>

                                    <x-flex xs4 :lg12="!column" mb-3>
                                        <x-select
                                            label="Viewable by"
                                            :items="insurers"
                                            v-model="selectedInsurers"
                                            placeholder="Viewable by"
                                            append-icon="keyboard_arrow_down"
                                            outline
                                            item-text="name"
                                            item-value="id"
                                            multiple
                                            hide-details
                                            :disabled="selectInsurersDisabled"
                                            :menu-props="{
                                                offsetY: true,
                                                maxHeight: 400
                                            }"
                                        >
                                            <template slot="selection" slot-scope="{item, index}">
                                                <span v-if="index === 0">{{ numberOfInsurersSelected }}</span>
                                            </template>

                                            <template slot="prepend-item">
                                                <x-list-tile class="buttons-top-tile no-hover">
                                                    <x-btn @click="selectAllInsurers" color="secondary">Select all</x-btn>
                                                    <x-btn @click="deselectAllInsurers" color="tertiary">Deselect all</x-btn>
                                                </x-list-tile>
                                            </template>
                                        </x-select>
                                    </x-flex>
                                </x-layout>

                                <x-layout mt-2>
                                    <x-flex xs12 text-xs-center>
                                        <x-btn
                                            :class="{ 'full-width': !column }"
                                            color="tertiary"
                                            @click="handleSpecificFileUpload"
                                        >
                                            <x-icon class="mr-2">add</x-icon>

                                            Add document
                                        </x-btn>
                                    </x-flex>
                                </x-layout>
                            </x-container>
                        </x-form>
                    </x-card-text>
                </x-card>
            </x-flex>

            <x-flex
                xs12
                :lg9="insurerSpecificUpload && !column"
                class="document-list"
                :class="{
                    'document-upload-list': upload,
                    'left': !upload,
                }"
            >
                <x-card class="panel">
                    <x-card-title>
                        <h3>Documents</h3>
                    </x-card-title>

                    <x-card-text>
                        <x-data-table
                            v-if="documents"
                            class="document-list-table no-row-hover"
                            :class="{ 'overflow-hidden': !documents.length }"
                            :headers="tableHeaders"
                            :items="documents"
                            hide-actions
                        >
                            <template slot="items" slot-scope="props">
                                <tr>
                                    <td>
                                        <file-icon :filename="props.item.filename" />
                                    </td>

                                    <td>
                                        {{ props.item.filename }}
                                    </td>

                                    <td>
                                        {{ props.item.document_category_name }}
                                    </td>

                                    <td>
                                        {{ props.item.created_at | formatDate }}
                                    </td>

                                    <td>
                                        {{ props.item.created_by_name }}
                                    </td>

                                    <td class="text-xs-right">
                                        <x-btn v-if="allowSetPermissions" color="tertiary" class="extra-small" @click="setPermissions(props.item.id)">Set permissions</x-btn>

                                        <i class="material-icons download-icon ml-2" @click="downloadDocument(props.item.id)">browser_updated</i>

                                        <x-btn v-if="allowDelete(props.item)" class="square-icon extra-small ml-2" color="tertiary" @click="deleteDocument(props.item)">
                                            <i class="material-icons">close</i>
                                        </x-btn>
                                    </td>
                                </tr>
                            </template>
                        </x-data-table>
                    </x-card-text>
                </x-card>

                <!-- todo: redesign - non-insurer specific file upload (facility documents etc.) -->
                <x-card v-if="upload && !insurerSpecificUpload" class="panel">
                    <x-card-title>
                        <h3>Upload files</h3>
                    </x-card-title>

                    <x-card-text>
                        <x-form class="document-upload" ref="uploadForm"
                                @submit.prevent="addDocument">
                            <x-layout v-if="!uploadInProgress">
                                <x-flex xs12>
                                    <x-select
                                        v-model="uploadDocument.category"
                                        class="document-list-upload-select"
                                        :items="documentCategories"
                                        placeholder="Choose a document category"
                                        append-icon="keyboard_arrow_down"
                                        hide-details
                                        :attach="attachMenu ? '.document-list-upload-select' : false"
                                        @input="categoryPicked"
                                    />

                                    <input
                                        ref="documentFileInput"
                                        type="file"
                                        @change="filePicked"
                                    >
                                </x-flex>
                            </x-layout>

                            <x-layout v-else>
                                <x-flex xs12>
                                    <progress-linear-bar
                                        label="Uploading"
                                        color="blue"
                                        small
                                        indeterminate
                                    />
                                </x-flex>
                            </x-layout>
                        </x-form>
                    </x-card-text>
                </x-card>
            </x-flex>
        </x-layout>

        <modal
            ref="uploadDocumentConfirmationModal"
            title="Add document"
            actionText="Confirm"
            confirmation
        >
            <div>
                <p>File name: {{ specificFileUploadDetails.name }}</p>

                <p>File type: {{ specificFileUploadDetails.type }}</p>

                <template v-if="specificFileUploadDetails.insurers.length > 0">
                    <p>Viewable by:</p>

                    <ul>
                        <li v-for="insurer in specificFileUploadDetails.insurers">{{ insurer }}</li>
                    </ul>
                </template>

                <p v-else>No insurers selected</p>
            </div>
        </modal>

        <modal ref="deleteModal" confirmation title="Delete document" action-text="Delete">
            <p>{{ activeDocument.filename }}</p>

            <p>Are you sure you want to delete this file?</p>
        </modal>

        <modal ref="fileTypeErrorModal" title="Error">
            <p>{{ fileTypeErrorMessage }}</p>
        </modal>

        <modal
            ref="setDocumentPermissionsModal"
            title="Permissions"
            actionText="Save"
            confirmation
        >
            <x-layout justify-center>
                <x-flex xs6>
                    <x-flex mt-4>
                        <p>Set document permission for: {{ selectedDocumentForPermissions }}</p>
                    </x-flex>

                    <x-flex mt-4>
                        <x-select
                            label="Viewable by"
                            :items="insurers"
                            v-model="selectedInsurers"
                            placeholder="Viewable by"
                            append-icon="keyboard_arrow_down"
                            outline
                            item-text="name"
                            item-value="id"
                            multiple
                            hide-details
                            :disabled="selectInsurersDisabled"
                            :menu-props="{
                                offsetY: true,
                                maxHeight: 400
                            }"
                        >
                            <template slot="selection" slot-scope="{item, index}">
                                <span v-if="index === 0">{{ numberOfInsurersSelected }}</span>
                            </template>

                            <template slot="prepend-item">
                                <x-list-tile class="buttons-top-tile no-hover">
                                    <x-btn @click="selectAllInsurers" color="success">Select all</x-btn>

                                    <x-btn @click="deselectAllInsurers" color="error">Deselect all</x-btn>
                                </x-list-tile>
                            </template>
                        </x-select>
                    </x-flex>
                </x-flex>
            </x-layout>
        </modal>
    </x-container>
</template>

<script>
import FileIcon from '~/components/Shared/UIComponents/FileIcon';
import ProgressLinearBar from '~/components/Broker/UIComponents/ProgressLinearBar';
import Modal from '~/components/Shared/UIComponents/Modal';
import rules from '~/mixins/rules';

export default {
    name: 'DocumentList',
    components: {
        FileIcon,
        ProgressLinearBar,
        Modal
    },
    mixins: [rules],
    props: {
        upload: Boolean,
        canDelete: {
            type: Function,
            default: () => true
        },
        documentableId: [String, Number],
        documentableType: String,
        documentableCategoryId: Number,
        attachMenu: Boolean,
        facilityId: Number,
        insurerSpecificUpload: Boolean,
        insurerList: {
            type: Array,
            default: () => []
        },
        allowPolicyWordingAndMRCHandling: {
            // this enables user to upload or delete policy wording and mrc documents
            type: Boolean,
            default: false
        },
        allowSetPermissions: {
            type: Boolean,
            required: false,
            default: false
        },
        mstDocuments: {
            type: Array,
            default: undefined,
            required: false
        },
        isPreFirmOrder: {
            type: Boolean,
            default: false
        },
        isStandalonePolicy: {
            type: Boolean,
            default: false
        },
        showMRCOnly: {
            type: Boolean,
            default: false
        },
        column: {
            type: Boolean,
            default: false
        },
    },
    data() {
        return {
            documents: [],
            activeDocument: {},
            uploadDocument: {
                file: null,
                category: null
            },
            uploadInProgress: false,
            documentsLoading: false,
            documentChanges: {
                isProcessing: false,
                selectedDocumentData: {}
            },
            tableHeaders: [
                {
                    // Document file icon
                    text: '',
                    value: '',
                    sortable: false,
                    width: '26px',
                },
                {
                    text: 'Document name',
                    value: 'filename'
                },
                {
                    text: 'Category',
                    value: 'document_type_name'
                },
                {
                    text: 'Date',
                    value: 'created_at'
                },
                {
                    text: 'Author',
                    value: 'created_by_name'
                },
                {
                    text: 'Action',
                    value: '',
                    align: 'right'
                }
            ],
            insurers: [],
            selectedInsurers: [],
            selectInsurersDisabled: false,
            fileTypeErrorMessage: '',
            selectedDocumentForPermissions: null,
            endorsementReferenceMissing: false
        }
    },
    computed: {
        documentCategories() {
            let docCategories = [];
            switch (this.documentableType) {
                case 'facility':
                    docCategories.push(
                        {
                            text: 'Facility',
                            value: 5
                        }
                    );
                    break;
                case 'endorsement':
                    docCategories.push(
                        {
                            text: 'Endorsement',
                            value: 6
                        },
                        {
                            value: 3,
                            text: 'Supporting Information'
                        },
                    );
                    break;
                case 'declaration':
                    docCategories.push(
                        {
                            value: 1,
                            text: 'Contract'
                        },
                        {
                            value: 2,
                            text: 'Repayment Schedule'
                        },
                        {
                            value: 3,
                            text: 'Supporting Information'
                        },
                        {
                            value: 4,
                            text: 'Other'
                        },
                        {
                            value: 7,
                            text: 'Declaration'
                        }
                    );
                    break;
                case 'mid_term_adjustment':
                    docCategories.push(
                        {
                            text: 'Endorsement',
                            value: 6
                        },
                        {
                            value: 3,
                            text: 'Supporting Information'
                        }
                    );
                    break;
                default:
                    if (this.showMRCOnly) {
                        docCategories.push({
                            value: 11,
                            text: 'Market Reform Contract'
                        });
                    } else {
                        if (!this.isStandalonePolicy) {
                            docCategories.push(
                                {
                                    value: 1,
                                    text: 'Contract'
                                }
                           );
                        }

                        docCategories.push(
                            {
                                value: 2,
                                text: 'Repayment Schedule'
                            },
                            {
                                value: 3,
                                text: 'Supporting Information'
                            },
                            {
                                value: 4,
                                text: 'Other'
                            }
                        );

                        if (this.allowPolicyWordingAndMRCHandling && this.isPreFirmOrder) {
                            docCategories.push({
                                    value: 10,
                                    text: 'Policy Wording'
                                },
                                {
                                    value: 11,
                                    text: 'Market Reform Contract'
                                });
                        }
                    }
            }


            if (this.documents && this.documents.length) {
                const addedDocCategoryIds = this.documents.map(document => document.document_category_id);

                // If there is already a policy wording document uploaded remove that category
                if (addedDocCategoryIds.includes(10)) {
                    docCategories = docCategories.filter(category => category.value !== 10);
                }

                // If there is already an MRC document uploaded remove that category
                if (addedDocCategoryIds.includes(11)) {
                    docCategories = docCategories.filter(category => category.value !== 11);
                }

                // If there is already an endorsement document uploaded remove that category
                if (addedDocCategoryIds.includes(6)) {
                    docCategories = docCategories.filter(category => category.value !== 6);
                }
            }

            return docCategories;
        },
        specificFileUploadDetails() {
            const file = {
                name: '',
                type: '',
            };
            if (this.uploadDocument.file) {
                file.name = this.uploadDocument.file.name;
            }
            if (this.uploadDocument.category) {
                const type = this.documentCategories.find(category => category.value === this.uploadDocument.category);
                if (type) {
                    file.type = type.text;
                }
            }
            file.insurers = this.humanReadableSelectedInsurers;
            return file;
        },
        humanReadableSelectedInsurers() {
            const readableSelectedInsurers = [];
            const {insurers} = this;
            this.selectedInsurers.forEach(function (selectedInsurer) {
                const insurerFound = insurers.find(insurer => insurer.id === selectedInsurer);
                readableSelectedInsurers.push(insurerFound.name);

            });
            return [...new Set(readableSelectedInsurers)];
        },
        filename() {
            return (this.uploadDocument && this.uploadDocument.file) ? this.uploadDocument.file.name : null;
        },
        numberOfInsurersSelected() {
            if (this.insurers.length === this.selectedInsurers.length) {
                return "All insurers selected";
            }
            return this.selectedInsurers.length + " insurers selected";
        }
    },
    mounted() {
        this.$eventBus.$on('documentsChanged', this.getDocuments);
        this.$eventBus.$on('delete-document', this.deleteDocument);

        this.getDocuments();
        this.resetUploadDocument();
        this.getInsurers();
    },
    beforeDestroy() {
        this.$eventBus.$off('documentsChanged', this.getDocuments);
        this.$eventBus.$off('delete-document', this.deleteDocument);
    },
    methods: {
        allowDelete(item) {
            // Disable deleting Market Reform Contract after preFirmOrder statuses
            if (item.document_category_id === 11 && !this.isPreFirmOrder) {
                return false;
            }

            //if allowPolicyWordingAndMRCHandling is enabled allow user to delete anything
            // otherwise prevent him from deleting policy wording (category id 10)
            return this.canDelete(item) && (this.allowPolicyWordingAndMRCHandling || this.isPreFirmOrder ? true : item.document_category_id !== 10);
        },
        async setPermissions(docId) {
            const selectedDocument = this.documents.find(doc => doc.id === docId);
            this.selectedDocumentForPermissions = selectedDocument.filename;
            let insurersWithPermissions = this.mstDocuments
                .find(document => document.id === docId)
                .permissions
                .map(permission => permission.viewable_by);

            insurersWithPermissions = [...new Set(insurersWithPermissions)];
            this.selectedInsurers = insurersWithPermissions.filter(insurerId => this.insurers.find(insurer => insurer.id === insurerId));

            if (await this.$refs.setDocumentPermissionsModal.open()) {
                this.uploadInProgress = true;

                axios.post(`/api/documents/${docId}/set-permissions`, { viewableBy: [...this.selectedInsurers] }).then(() => {
                    this.$eventBus.$emit('documentsChanged');
                }).then(() => {
                    this.uploadInProgress = false;
                });
            }

            this.deselectAllInsurers();
            this.selectedDocumentForPermissions = null;
        },
        getDocumentCategoryName(categoryId) {
            return (this.documentCategories.find(cat => cat.value === categoryId)).text;
        },
        getDocuments() {
            this.documentsLoading = true;
            const url = (!this.upload) ? `/api/insurer/documents/${this.documentableType}/${this.documentableId}`
                : `/api/documents/${this.documentableType}/${this.documentableId}`
            axios.get(url).then(res => {
                this.documents = res.data.data || [];
                this.$emit("update-documents", this.documents);
                this.documentsLoading = false;
            });
        },
        resetUploadDocument() {
            this.uploadDocument = {
                file: null,
                category: null
            };
        },
        categoryPicked() {
            this.$refs.documentFileInput.click();
        },
        specificFilePicked() {
            this.$refs.specificFile.click();
        },
        loadSpecificFile(e) {
            // eslint-disable-next-line prefer-destructuring
            this.uploadDocument.file = e.target.files[0];
        },
        async handleSpecificFileUpload() {
            if (!this.$refs.specificDocumentForm.validate()) {
                return;
            }
            if (await this.$refs.uploadDocumentConfirmationModal.open() === true) {
                if (typeof this.uploadDocument.file.name !== 'undefined') {
                    if (this.uploadDocument.file.name.lastIndexOf('.') <= 0) {
                        return 0;
                    }
                    const fr = new FileReader();
                    fr.readAsDataURL(this.uploadDocument.file);
                    fr.addEventListener('load', () => {
                        this.addDocument()
                    });
                }

            }
        },
        filePicked(e) {
            const pickedFile = e.target.files[0];

            if (pickedFile !== undefined) {
                if (pickedFile.name.lastIndexOf('.') <= 0) {
                    return;
                }

                const fr = new FileReader();
                fr.readAsDataURL(pickedFile);
                fr.addEventListener('load', () => {
                    this.uploadDocument.file = pickedFile;
                    this.addDocument();
                });
            } else {
                resetUploadDocument();
            }
        },
        addDocument() {
            if (!this.fileExtensionIsValid()) {
                this.fileTypeErrorMessage = `Please upload the ${this.getDocumentCategoryName(this.uploadDocument.category)} with PDF extension`;
                this.$refs.fileTypeErrorModal.open();
                return false;
            }

            this.uploadInProgress = true;

            const self = this;
            const url = (this.insurerSpecificUpload) ? `/api/insurer/documents/${this.documentableType}/${this.documentableId}`
                : `/api/documents/${this.documentableType}/${this.documentableId}`;
            const data = new FormData();
            data.append('document', this.uploadDocument.file);
            data.append('category', this.uploadDocument.category);
            if (this.insurerSpecificUpload) {
                data.append('viewableBy', this.selectedInsurers)
            }
            const config = {
                headers: {'Content-Type': 'multipart/form-data'},
                onUploadProgress(progressEvent) {
                    this.uploadPercentage = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                }
            };

            axios.post(url, data, config).then(() => {
                this.$eventBus.$emit('documentsChanged');
            }).then(() => {
                self.resetUploadDocument();
                self.uploadInProgress = false;
            });
        },
        async downloadDocument(documentId) {
            const documentCategoryKey = this.documents.find(document => document.id === documentId).document_category_key;
            if (this.documentableType === 'endorsement' && documentCategoryKey === 'endorsement') {
                return this.downloadEndorsementDocument(documentId);
            }

            const apiPrefix = this.$root.user.company.company_type === 'insurer' || this.$root.user.company.company_type === 'captive_insurer' ? 'insurer' : 'broker';
            const fileUrl = (await axios.get(`/api/${apiPrefix}/documents/${documentId}`)).data.url;

            window.location.href = fileUrl;
        },
        async downloadEndorsementDocument(documentId) {
            const fileUrl = (await axios.get(`/api/endorsement/${this.documentableId}/documents/endorsement-download`)).data.url;

            window.location.href = fileUrl;
        },
        async deleteDocument(document) {
            this.activeDocument = document;

            if (await this.$refs.deleteModal.open() === true) {
                await axios.delete(`/api/documents/${this.activeDocument.id}`);
                this.getDocuments();
            }
        },
        getInsurers() {
            if (this.insurerList.length > 0) {
                this.fillDefaultInsurers(this.insurerList);
            } else {
                const url = (this.facilityId !== null) ? `/api/insurer/facility/${this.facilityId}` : `/api/insurer`;
                axios.get(url).then(res => {
                    this.fillDefaultInsurers(res.data.data);
                });
            }
        },
        fillDefaultInsurers(insurerList) {
            this.insurers = insurerList
            this.selectedInsurers = this.insurers.map(insurer => insurer.id);
        },
        selectAllInsurers() {
            this.selectedInsurers = this.insurers.map(insurer => insurer.id);
        },
        deselectAllInsurers() {
            this.selectedInsurers = [];
        },
        isInsurersChanged(oldInsurers, newInsurers) {
            if (oldInsurers.length !== newInsurers.length) {
                return true;
            }

            return oldInsurers.some(function (insurer) {
                return !newInsurers.includes(insurer);

            });

        },
        fileExtensionIsValid() {
            if (this.isMarketReformContractType() || this.isPolicyWordingType() || this.isEndorsementType()) {
                return this.uploadDocument.file.type === 'application/pdf';
            }

            return true;
        },
        isPolicyWordingType() {
            return [10, 12].includes(this.uploadDocument.category);
        },
        isMarketReformContractType() {
            return [11, 13].includes(this.uploadDocument.category);
        },
        isEndorsementType() {
            return [6].includes(this.uploadDocument.category);
        },
        async checkForEndorsementReference() {
            let endorsementReferenceExists;

            await axios.get(`/api/broker/endorsement/${this.documentableId}/has-reference`)
                .then((response) => {
                    endorsementReferenceExists = response.data.has_reference;
                });

            return endorsementReferenceExists;
        },
    },

    watch: {
        documents: {
            handler() {
                this.documents.forEach((document) => {
                    this.$set(
                        this.documentChanges.selectedDocumentData,
                        document.id,
                        document
                    );
                });
            },
            immediate: true,
            deep: true,
        },
        documentsLoading: {
            handler() {
                this.$emit("documentsLoading", this.documentsLoading);
            },
            immediate: true
        },
        documentChanges: {
            handler() {
                this.$emit("documentChanges", this.documentChanges);
            },
            immediate: true,
            deep: true
        },
        uploadInProgress: {
            handler() {
                this.documentChanges.isProcessing = this.uploadInProgress;
            },
            immediate: true
        },
        uploadDocument: {
            handler() {
                if (this.uploadDocument.category && (this.isPolicyWordingType() || this.isMarketReformContractType())) {
                    this.selectAllInsurers();
                    this.selectInsurersDisabled = true;
                    return;
                }

                this.selectInsurersDisabled = false;
            },
            deep: true
        },
        documentCategories(newVal) {
            this.$emit('update-document-categories', newVal);
        },
        insurerList(newVal) {
            this.fillDefaultInsurers(newVal);
        },
    },
}
</script>

<style lang="scss" scoped>
.document-list {
    .document-list-table {
        :deep(.x-table__overflow) {
            overflow-y: auto;
        }

        tbody {
            tr {
                height: 76px;

                td {
                    .x-btn {
                        height: 32px;
                    }

                    .download-icon {
                        width: 32px;
                        height: 32px;
                        font-size: 32px;
                        cursor: pointer;
                    }
                }
            }
        }
    }

    &.document-upload-list {
        .document-list-table {
            tbody {
                tr {
                    td {
                        > * {
                            vertical-align: middle;
                        }
                    }
                }
            }
        }
    }
}

form {
    input[type=file] {
        display: none;
    }

    .progress-linear-bar {
        margin: 0.5rem 0;

        > :deep(span) {
            font-family: $font-body;
        }
    }
}

form {
    &.document-upload {
        max-width: 450px;
        padding: 25px;
        margin: 0 auto;

        .x-select.x-input.x-text-field :deep(.x-input__control .x-input__slot) {
            margin: 0;
        }
    }
}
article.specific-document-upload {
    form {
        .layout {
            &.add-document-wrapper {
                margin-bottom: 0 !important;

                button {
                    display: block;
                    margin: 20px auto;
                }
            }

            .flex {
                padding: 0;

                &:not(:last-child) {
                    margin-right: 10px;
                }
            }
        }

    }
}
.x-select-list {
    :deep(.x-list) {
        .x-list__tile {
            margin: 2px;
            padding: 15px;
            border-radius: $border-radius-small;
        }

        .buttons-top-tile {
            .x-list__tile {
                justify-content: space-between;
                margin: -7px -7px 0;
                padding-top: 0;
                padding-bottom: 0;

                button {
                    flex-grow: 0;
                    width: 120px;
                }
            }
        }

        .overflow-hidden :deep(.x-table__overflow) {
            overflow-x: hidden;
        }
    }
}

@media only screen and (min-width: 1264px) {
    .full-width {
        width: 100%;
    }
}
</style>
